import { useEffect, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin, Activity, HeartPulse, FileText, Package, Trophy, CalendarClock, AlertTriangle, Clock, CheckCircle2, ChevronRight, ChevronLeft, ChevronDown, Calendar as CalendarIcon, Coffee, FilePenLine, ImageDown, FileDown, Share2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { safeFormatDate, safeFormatDateOnly } from "@/lib/dates";
import { downloadDailySummaryImage, downloadDailySummaryPDF, shareDailySummaryImage } from "@/lib/imageDailySummary";
import { toast } from "sonner";
import { api } from "@/lib/api";

// SAST = UTC+2. We build "today in SAST" without relying on the browser's
// local zone so operators travelling see the same anchor date the backend
// uses. Returns YYYY-MM-DD.
function sastTodayISO() {
  const now = new Date();
  const sast = new Date(now.getTime() + (now.getTimezoneOffset() + 120) * 60_000);
  const yr = sast.getFullYear();
  const mo = String(sast.getMonth() + 1).padStart(2, "0");
  const dy = String(sast.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dy}`;
}

/**
 * Full daily-changes report — every detail behind the dashboard glance card.
 * Supports a date picker so leadership can look back at any past SAST day.
 */
export default function ChangesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => sastTodayISO());

  const load = async (dateISO) => {
    setLoading(true);
    try {
      const isToday = dateISO === sastTodayISO();
      const params = isToday ? {} : { date: dateISO };
      const res = await api.get("/dashboard/daily-summary", { params });
      setData(res.data);
    } catch (e) {
      toast.error("Couldn't load daily changes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(selectedDate); /* eslint-disable-next-line */ }, [selectedDate]);
  useQuickEditRefresh(() => load(selectedDate));

  const isToday = selectedDate === sastTodayISO();
  const goToday = () => setSelectedDate(sastTodayISO());
  const shiftDay = (deltaDays) => {
    const d = new Date(`${selectedDate}T12:00:00Z`);
    d.setDate(d.getDate() + deltaDays);
    const yr = d.getUTCFullYear();
    const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dy = String(d.getUTCDate()).padStart(2, "0");
    setSelectedDate(`${yr}-${mo}-${dy}`);
  };

  const markPaid = async (id) => {
    setActingId(id);
    try {
      await api.post(`/invoices/${id}/mark-paid`);
      toast.success("Marked as paid");
      load(selectedDate);
    } finally { setActingId(null); }
  };
  const snooze = async (id) => {
    setActingId(id);
    try {
      await api.post(`/invoices/${id}/snooze`, { days: 7 });
      toast.success("Snoozed 7 days");
      load(selectedDate);
    } finally { setActingId(null); }
  };

  // Download the branded "Today at a glance" image for the currently
  // selected day. Uses the already-loaded `data` (which the date picker
  // keeps in sync), so previous days export exactly what's on screen.
  const exportImage = async () => {
    if (exporting || !data) return;
    setExporting(true);
    try {
      const label = safeFormatDateOnly(data.today_sast, "EEEE, d MMMM yyyy", isToday ? "today" : selectedDate);
      const { pages } = await downloadDailySummaryImage(data, { todayLabel: label });
      toast.success(pages > 1 ? `Downloaded ${pages} images` : "Image downloaded");
    } catch (e) {
      console.error("[glance] image export failed:", e);
      toast.error(`Could not generate the image: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  // Download the full day as a clean multi-page PDF.
  const exportPDF = async () => {
    if (exportingPdf || !data) return;
    setExportingPdf(true);
    try {
      const label = safeFormatDateOnly(data.today_sast, "EEEE, d MMMM yyyy", isToday ? "today" : selectedDate);
      const { pages } = await downloadDailySummaryPDF(data, { todayLabel: label });
      toast.success(pages > 1 ? `PDF ready · ${pages} pages` : "PDF downloaded");
    } catch (e) {
      console.error("[glance] pdf export failed:", e);
      toast.error(`Could not generate the PDF: ${e?.message || e}`);
    } finally {
      setExportingPdf(false);
    }
  };

  // Share the day's glance image straight into a WhatsApp chat.
  const shareWhatsApp = async () => {
    if (sharing || !data) return;
    setSharing(true);
    try {
      const label = safeFormatDateOnly(data.today_sast, "EEEE, d MMMM yyyy", isToday ? "today" : selectedDate);
      const { method } = await shareDailySummaryImage(data, { todayLabel: label });
      if (method === "share") toast.success("Choose a WhatsApp chat to send it");
      else if (method === "fallback") toast.success("Image saved — attach it in the WhatsApp chat that opened");
      // method === "cancelled": user dismissed the share sheet — stay quiet.
    } catch (e) {
      console.error("[glance] whatsapp share failed:", e);
      toast.error(`Could not share to WhatsApp: ${e?.message || e}`);
    } finally {
      setSharing(false);
    }
  };

  // Full-page loader ONLY on the very first fetch. Subsequent date-picker
  // changes render the shell + a subtle inline spinner in the picker so the
  // page doesn't blank out on every navigation.
  if (loading && !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading changes…
        </div>
      </div>
    );
  }
  if (!data) return null;

  const horses = data.horses || {};
  const invoices = data.invoices || {};
  const feedTrend = data.feed_trend || [];
  const chukkas = data.chukkas || {};
  const bills = data.bills || {};

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header + date picker */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors" data-testid="changes-back-link">
            <ArrowLeft className="h-3 w-3" /> Dashboard
          </Link>
          <h1 className="font-serif text-3xl mt-2" data-testid="changes-title">
            {isToday ? "Today's changes" : "Changes"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Full activity report for {safeFormatDateOnly(data.today_sast, "EEEE, MMMM d, yyyy", isToday ? "today" : selectedDate)} (SAST).
          </p>
        </div>
        {/* Date picker + shortcut buttons */}
        <div className="flex flex-col gap-2" data-testid="changes-date-picker">
          <div className="flex items-center gap-2">
            <div className="relative">
              <CalendarIcon className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="date"
                value={selectedDate}
                max={sastTodayISO()}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="pl-8 w-[170px]"
                data-testid="changes-date-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => shiftDay(-1)} className="h-8 text-xs" data-testid="changes-date-prev">
              <ChevronLeft className="h-3 w-3" /> Prev day
            </Button>
            <Button variant="outline" size="sm" onClick={() => shiftDay(1)} disabled={isToday} className="h-8 text-xs" data-testid="changes-date-next">
              Next day <ChevronRight className="h-3 w-3" />
            </Button>
            <Button variant={isToday ? "default" : "outline"} size="sm" onClick={goToday} className="h-8 text-xs" data-testid="changes-date-today">
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportImage}
              disabled={exporting || !data}
              className="h-8 text-xs"
              data-testid="changes-download-image"
              aria-label="Download this day's summary as an image"
            >
              {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageDown className="h-3 w-3" />}
              <span className="ml-1">Image</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportPDF}
              disabled={exportingPdf || !data}
              className="h-8 text-xs"
              data-testid="changes-download-pdf"
              aria-label="Download this day's summary as a PDF"
            >
              {exportingPdf ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
              <span className="ml-1">PDF</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={shareWhatsApp}
              disabled={sharing || !data}
              className="h-8 text-xs text-[hsl(var(--ap-forest))] border-[hsl(var(--ap-forest)/0.4)] hover:bg-[hsl(var(--ap-forest)/0.08)]"
              data-testid="changes-share-whatsapp"
              aria-label="Share this day's summary to WhatsApp"
            >
              {sharing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Share2 className="h-3 w-3" />}
              <span className="ml-1">WhatsApp</span>
            </Button>
          </div>
          {loading && (
            <div className="text-xs text-muted-foreground flex items-center gap-1" data-testid="changes-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </div>
          )}
        </div>
      </div>

      {/* Empty-day state. makes it obvious the day just has no activity
          (rather than looking like a broken page). */}
      {!loading &&
        !horses.added?.count_today &&
        !horses.location_changes?.length &&
        !horses.work_status_changes?.length &&
        !horses.injury_changes?.length &&
        !invoices.count_today &&
        !feedTrend.length &&
        !chukkas.count_today && (
        <Card className="rounded-xl border-dashed" data-testid="changes-empty-state">
          <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
            <Coffee className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
            <div className="text-sm text-muted-foreground">
              {isToday
                ? "Quiet day so far. No new activity since midnight."
                : "No activity recorded for this day."}
            </div>
            {!isToday && (
              <Button variant="outline" size="sm" onClick={goToday} data-testid="changes-empty-back-today">
                Back to today
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Horses added */}
      {horses.added?.count_today > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-horses-added">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} /> New horses added
            </CardTitle>
            <Badge>{horses.added.count_today} today</Badge>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {horses.added.names.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {horses.added.names.map((n) => (
                  <Link key={n} to={`/horses?q=${encodeURIComponent(n)}`}
                    className="px-3 py-1 rounded-full bg-muted hover:bg-[hsl(var(--ap-forest))]/10 text-sm transition-colors"
                    data-testid={`changes-new-horse-${n}`}
                  >
                    {n}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">{horses.added.count_today} horses (names not recorded)</div>
            )}
            {horses.added.count_yesterday > 0 && (
              <div className="text-xs text-muted-foreground mt-2">
                vs {horses.added.count_yesterday} yesterday
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Location changes */}
      {horses.location_changes?.length > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-location">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Horses moved
            </CardTitle>
            <Badge>{horses.location_changes.length}</Badge>
          </CardHeader>
          <CardContent className="space-y-2">
            {horses.location_changes.map((m, i) => (
              <ChangeRow key={`loc-${m.horse}-${m.from}-${m.to}-${i}`} horse={m.horse} horseId={m.horse_id} from={m.from} to={m.to} testId={`changes-location-${i}`} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Work status changes */}
      {horses.work_status_changes?.length > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-workstatus">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Activity className="h-4 w-4" /> Work-status changes
            </CardTitle>
            <Badge>{horses.work_status_changes.length}</Badge>
          </CardHeader>
          <CardContent className="space-y-2">
            {horses.work_status_changes.map((m, i) => (
              <ChangeRow key={`ws-${m.horse}-${m.from}-${m.to}-${i}`} horse={m.horse} horseId={m.horse_id} from={m.from} to={m.to} testId={`changes-workstatus-${i}`} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Injury updates */}
      {horses.injury_changes?.length > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-injury">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <HeartPulse className="h-4 w-4 text-red-600" /> Injury updates
            </CardTitle>
            <Badge>{horses.injury_changes.length}</Badge>
          </CardHeader>
          <CardContent className="space-y-2">
            {horses.injury_changes.map((m, i) => (
              <div key={`inj-${m.horse}-${m.injured_to}-${m.severity_to || ""}-${i}`} className="text-sm flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/40">
                {m.horse_id ? (
                  <Link to={`/horses/${m.horse_id}`} className="font-medium min-w-[100px] hover:underline underline-offset-2" data-testid={`changes-injury-${i}-horse-link`}>{m.horse}</Link>
                ) : (
                  <span className="font-medium min-w-[100px]">{m.horse}</span>
                )}
                <span className="text-muted-foreground">
                  {m.injured_to === true && (
                    <>Now injured {m.severity_to && <strong className="text-foreground">({m.severity_to})</strong>}{m.comments && <>. <em>{m.comments}</em></>}</>
                  )}
                  {m.injured_to === false && <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Recovered</span>}
                  {m.injured_to == null && m.severity_to && <>Severity → <strong className="text-foreground">{m.severity_to}</strong></>}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Invoices uploaded today */}
      {invoices.count_today > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-invoices">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" /> Invoices uploaded
            </CardTitle>
            <Badge>{invoices.count_today}</Badge>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-baseline gap-3 mb-2">
              {Object.entries(invoices.totals_today || {}).map(([cur, amt]) => (
                <div key={cur} className="font-serif text-xl tabular-nums">
                  {formatCurrency(amt, cur)}
                </div>
              ))}
              {invoices.count_yesterday > 0 && (
                <div className="text-xs text-muted-foreground">
                  · vs {Object.entries(invoices.totals_yesterday || {}).map(([cur, amt]) => formatCurrency(amt, cur)).join(", ")} yesterday
                </div>
              )}
            </div>
            {(invoices.items_today || []).map((inv) => (
              <Link key={inv.id} to="/invoices" className="block rounded-md border px-3 py-2 hover:bg-muted/40 transition-colors" data-testid={`changes-invoice-${inv.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{inv.supplier}</div>
                    <div className="text-xs text-muted-foreground capitalize">{inv.category || "uncategorised"}</div>
                  </div>
                  <div className="text-sm tabular-nums">
                    {inv.total != null ? formatCurrency(inv.total, inv.currency) : ""}
                  </div>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Feed trend */}
      {feedTrend.length > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-feed">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Package className="h-4 w-4" /> Feed used today
            </CardTitle>
            <Link to="/feed" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              See feed page <ChevronRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {feedTrend.map((ft) => {
              const arrow = ft.delta > 0 ? "↑" : ft.delta < 0 ? "↓" : "→";
              const tint = ft.delta > 0 ? "text-amber-700" : ft.delta < 0 ? "text-emerald-700" : "text-muted-foreground";
              return (
                <div key={ft.item} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted/40">
                  <div className="capitalize text-sm font-medium">{ft.item}</div>
                  <div className="text-sm text-muted-foreground flex items-baseline gap-3 tabular-nums">
                    <span><strong className="text-foreground">{ft.used_today}</strong> {ft.unit} today</span>
                    <span className="text-muted-foreground/70">·</span>
                    <span>{ft.used_yesterday} yesterday</span>
                    {ft.delta_pct != null && (
                      <span className={`font-medium ${tint}`}>
                        {arrow} {Math.abs(ft.delta_pct).toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Chukkas */}
      {chukkas.count_today > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-chukkas">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Trophy className="h-4 w-4" /> Horses ridden
            </CardTitle>
            <Badge>{chukkas.count_today}</Badge>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {/* Full list — user asked for every horse ridden today, not
                just the top-scored ones. Each row is a Link to the
                horse's individual page (falls back to plain text if
                the backend didn't send a horse_id, e.g. legacy rows). */}
            {(chukkas.ridden_today || chukkas.top_today || []).map((c, i) => {
              const key = `ridden-${c.horse_id || c.horse}-${c.score ?? "n"}-${i}`;
              const scoreLabel = c.score != null
                ? (typeof c.score === "number" ? c.score.toFixed(2) : c.score)
                : "—";
              const rowInner = (
                <div className="flex items-center justify-between text-sm px-2 py-1.5 rounded hover:bg-muted/40 transition-colors">
                  <span className="font-medium">{c.horse}</span>
                  <div className="flex items-center gap-3 text-muted-foreground text-xs tabular-nums">
                    {c.rider && <span>{c.rider}</span>}
                    <span className="text-foreground font-medium">{scoreLabel}</span>
                  </div>
                </div>
              );
              return c.horse_id ? (
                <Link
                  key={key}
                  to={`/horses/${c.horse_id}`}
                  className="block hover:no-underline"
                  data-testid={`changes-ridden-${i}-horse-link`}
                >
                  {rowInner}
                </Link>
              ) : (
                <div key={key} data-testid={`changes-ridden-${i}`}>{rowInner}</div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Other horse updates — per-field diffs that don't fit into the
          categorised cards above (e.g. purchase price edits, sex
          corrections, breed changes, notes updates, etc.). Expandable
          per-horse so the card doesn't blow up on days with lots of
          admin work; each horse name links to its detail page. */}
      {horses.other_changes?.length > 0 && (
        <Card className="rounded-xl" data-testid="changes-section-other">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FilePenLine className="h-4 w-4" /> Other horse updates
            </CardTitle>
            <Badge>{horses.other_changes.length}</Badge>
          </CardHeader>
          <CardContent className="space-y-1">
            {horses.other_changes.map((entry, i) => (
              <OtherChangeRow
                key={`oth-${entry.horse_id || entry.horse}-${i}`}
                entry={entry}
                testId={`changes-other-${i}`}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Bills to follow up */}
      {(bills.overdue?.length > 0 || bills.upcoming?.length > 0) ? (
        <Card className="rounded-xl" data-testid="changes-section-bills">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <CalendarClock className="h-4 w-4" /> Bills to follow up
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              {bills.overdue.length} overdue · {bills.upcoming.length} upcoming
            </span>
          </CardHeader>
          <CardContent className="space-y-2">
            {bills.overdue.map((b) => (
              <BillRow key={b.id} bill={b} variant="overdue"
                acting={actingId === b.id}
                onPay={() => markPaid(b.id)} onSnooze={() => snooze(b.id)} />
            ))}
            {bills.upcoming.map((b) => (
              <BillRow key={b.id} bill={b} variant="upcoming"
                acting={actingId === b.id}
                onPay={() => markPaid(b.id)} onSnooze={() => snooze(b.id)} />
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl bg-emerald-50/30 border-emerald-200/50">
          <CardContent className="py-4 flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            No outstanding bills due in the next 30 days.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-full bg-muted text-muted-foreground">
      {children}
    </span>
  );
}

function ChangeRow({ horse, horseId, from, to, testId }) {
  // The horse name links straight through to its individual page —
  // consistent with the "Horses ridden" list and the new user
  // expectation that every horse mention on ChangesPage is clickable.
  // Falls back to plain text if we don't have an id (legacy rows).
  const name = horseId ? (
    <Link
      to={`/horses/${horseId}`}
      className="font-medium min-w-[120px] hover:underline underline-offset-2 text-foreground"
      data-testid={`${testId}-horse-link`}
    >
      {horse}
    </Link>
  ) : (
    <span className="font-medium min-w-[120px]">{horse}</span>
  );
  return (
    <div className="text-sm flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/40" data-testid={testId}>
      {name}
      <span className="text-muted-foreground italic">{from || ""}</span>
      <span className="text-muted-foreground">→</span>
      <span className="font-medium">{to || ""}</span>
    </div>
  );
}


// Fields we deliberately hide from the "Other updates" panel because
// they'd add noise regardless of viewer role. Financial fields
// (purchase_price / listed_price / purchased_from / purchase_date)
// are stripped SERVER-SIDE for non-privileged users (see
// server.py -> other_horse_changes construction). We deliberately
// do NOT hide them here so admins / executives / masters CAN see
// price diffs when reviewing an audit trail — the backend enforces
// the privilege boundary, this list is just noise reduction.
const _OTHER_CHANGES_HIDDEN_FIELDS = new Set([
  "updated_at", "created_at",
  "photo", "photos",
  "ranking",           // recomputed automatically — noise
  "ranking_override",  // covered by its own audit line
]);

// Friendlier labels for common horse fields. Anything not in this map
// falls back to snake_case → Title case at render time.
const _FIELD_LABELS = {
  age: "Age",
  breed: "Breed",
  sex: "Sex",
  color: "Colour",
  notes: "Notes",
  purchase_price: "Purchase price",
  listed_price: "Listed price",
  purchased_from: "Purchased from",
  purchase_date: "Purchase date",
  status: "Status",
  for_sale: "For sale",
  microchip: "Microchip",
  passport: "Passport",
  registration: "Registration",
};

function prettifyField(k) {
  if (_FIELD_LABELS[k]) return _FIELD_LABELS[k];
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettifyValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}


function OtherChangeRow({ entry, testId }) {
  // Collapsible per horse — the header shows count + horse name, and
  // clicking reveals every changed field with a proper before → after
  // rendering. Defaults collapsed so a busy day doesn't produce a
  // 20-line-tall card.
  const [open, setOpen] = useState(false);
  const fields = Object.entries(entry.changes || {})
    .filter(([k]) => !_OTHER_CHANGES_HIDDEN_FIELDS.has(k));
  if (fields.length === 0) return null;
  return (
    <div className="rounded-md border border-border/70 bg-background overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-muted/40 transition-colors"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
          {entry.horse_id ? (
            <Link
              to={`/horses/${entry.horse_id}`}
              className="font-medium hover:underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${testId}-horse-link`}
            >
              {entry.horse}
            </Link>
          ) : (
            <span className="font-medium">{entry.horse}</span>
          )}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {fields.length} field{fields.length !== 1 ? "s" : ""} changed
        </span>
      </button>
      {open && (
        <div className="border-t border-border/50 bg-muted/20 divide-y divide-border/40">
          {fields.map(([field, diff]) => {
            const from = diff?.from;
            const to = diff?.to;
            return (
              <div
                key={field}
                className="px-4 py-2 text-sm grid grid-cols-[minmax(0,140px)_1fr] gap-3 items-baseline"
                data-testid={`${testId}-field-${field}`}
              >
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  {prettifyField(field)}
                </span>
                <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                  <span className="text-muted-foreground italic line-through decoration-muted-foreground/40 break-all">
                    {prettifyValue(from)}
                  </span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-medium break-all">{prettifyValue(to)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BillRow({ bill, variant, acting, onPay, onSnooze }) {
  const isOverdue = variant === "overdue";
  const tint = isOverdue
    ? "border-red-300/50 bg-red-50/40"
    : bill.days_until_due <= 7
      ? "border-amber-300/50 bg-amber-50/40"
      : "border-border bg-background";
  const Icon = isOverdue ? AlertTriangle : Clock;
  const iconColor = isOverdue ? "text-red-600" : (bill.days_until_due <= 7 ? "text-amber-600" : "text-muted-foreground");

  let dueLabel;
  if (bill.days_until_due < 0) dueLabel = `${Math.abs(bill.days_until_due)}d overdue`;
  else if (bill.days_until_due === 0) dueLabel = "Due today";
  else if (bill.days_until_due === 1) dueLabel = "Due tomorrow";
  else dueLabel = `Due in ${bill.days_until_due}d`;

  return (
    <div className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${tint}`} data-testid={`changes-bill-${bill.id}`}>
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${iconColor}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">
            {bill.supplier}
            {bill.invoice_number && <span className="text-muted-foreground font-normal ml-2 text-xs">#{bill.invoice_number}</span>}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>{dueLabel}</span>
            <span className="text-muted-foreground/60">·</span>
            <span>{safeFormatDate(bill.due_date, "MMM d, yyyy")}</span>
            {bill.total != null && (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span className="tabular-nums font-medium text-foreground">
                  {formatCurrency(bill.total, bill.currency)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="ghost" disabled={acting} onClick={onSnooze} className="h-8 px-2 text-xs">
          Snooze 7d
        </Button>
        <Button size="sm" disabled={acting} onClick={onPay} className="h-8 px-3 text-xs bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
          {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : <>Mark paid</>}
        </Button>
      </div>
    </div>
  );
}
